/**
 * PRIVACY POLICY PAGE
 * 
 * Standard privacy policy for the platform
 * Should be reviewed by legal counsel before launch
 */

import { Shield } from 'lucide-react';
import { PageHeader } from '../components/common';

export default function PrivacyPage() {
  const lastUpdated = 'July 2026';

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={Shield}
          title="Privacy Policy"
          subtitle={`Last updated: ${lastUpdated}`}
          className="mb-6"
        />

        {/* Content */}
        <div className="glass-card p-8 space-y-8 font-body">
          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Introduction</h2>
            <p className="text-glass-secondary leading-relaxed">
              Polish ALL the Things ("we", "us", or "our") respects your privacy and is committed to protecting 
              your personal data. This Privacy Policy explains how we collect, use, and safeguard your information 
              when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Information We Collect</h2>
            
            <h3 className="font-bold text-glass-secondary mt-4 mb-2">Information You Provide</h3>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Account information (email, username, display name)</li>
              <li>Profile information (avatar, bio, social links)</li>
              <li>Collection data (items, photos, notes)</li>
              <li>Messages and communications with other users</li>
              <li>Feedback and support requests</li>
            </ul>

            <h3 className="font-bold text-glass-secondary mt-4 mb-2">Information Collected Automatically</h3>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Device information (browser type, operating system)</li>
              <li>Usage data (pages visited, features used)</li>
              <li>IP address and general location</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">How We Use Your Information</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              We use your information to:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Provide and maintain the Service</li>
              <li>Enable features like collection management and marketplace</li>
              <li>Facilitate communication between users</li>
              <li>Send important service notifications</li>
              <li>Improve and personalize your experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Information Sharing</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              We do not sell your personal information. We may share information:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li><strong>With other users:</strong> Based on your privacy settings (public profile, wishlist, etc.)</li>
              <li><strong>Service providers:</strong> Companies that help us operate the Service (hosting, analytics)</li>
              <li><strong>Legal requirements:</strong> When required by law or to protect rights and safety</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Your Privacy Controls</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              You have control over your privacy through Settings:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li><strong>Profile visibility:</strong> Choose public or private</li>
              <li><strong>Wishlist visibility:</strong> Control who sees your wishlist</li>
              <li><strong>Collection visibility:</strong> Manage what others can see</li>
              <li><strong>Contact preferences:</strong> Control who can message you</li>
              <li><strong>Data export:</strong> Request a copy of your data</li>
              <li><strong>Account deletion:</strong> Delete your account and all data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Data Security</h2>
            <p className="text-glass-secondary leading-relaxed">
              We implement appropriate security measures to protect your information, including encryption, 
              secure servers, and access controls. However, no method of transmission over the Internet is 
              100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Data Retention</h2>
            <p className="text-glass-secondary leading-relaxed">
              We retain your information as long as your account is active or as needed to provide services.
              When you delete your account, we will delete your personal data within 30 days, except where
              retention is required by law.
            </p>
          </section>

          <section id="data-deletion">
            <h2 className="text-xl font-bold text-glass mb-3">Data Deletion</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              You can delete your data at any time — either individual items or your entire account.
            </p>

            <h3 className="font-bold text-glass-secondary mt-4 mb-2">Deleting Individual Items</h3>
            <p className="text-glass-secondary leading-relaxed mb-2">
              Open the app or website, tap or click the item you want to remove, and use the delete option
              in its menu. This works for:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Collection items (polish, plates, tools, powders, embellishments, decals, acrylics, cuticle care)</li>
              <li>Photos on any item</li>
              <li>Wishlist entries</li>
              <li>Marketplace listings (by un-listing or deleting the item)</li>
              <li>Messages and conversations</li>
              <li>Creations / manicures</li>
              <li>Profile fields (bio, location, profile photo, shipping preferences)</li>
            </ul>

            <h3 className="font-bold text-glass-secondary mt-4 mb-2">Deleting Your Account</h3>
            <p className="text-glass-secondary leading-relaxed">
              Go to <strong>Settings → Delete Account</strong>. After confirming, your account is scheduled
              for permanent deletion after a 30-day grace period. You can reactivate at any time during
              those 30 days by signing back in. After 30 days, your personal data is permanently removed,
              except for anonymized aggregate statistics and any records we're required to retain by law.
            </p>

            <h3 className="font-bold text-glass-secondary mt-4 mb-2">If You Can't Access the App</h3>
            <p className="text-glass-secondary leading-relaxed">
              If you're unable to sign in, email{' '}
              <a href="mailto:hello@polishallthethings.com" className="text-purple-300 underline">
                hello@polishallthethings.com
              </a>{' '}
              from the address associated with your account. Include your username if you remember it.
              We'll verify and process your request within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Cookies</h2>
            <p className="text-glass-secondary leading-relaxed">
              We use cookies and similar technologies to maintain your session, remember preferences, and 
              analyze usage. You can control cookies through your browser settings, but some features may 
              not function properly without them.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Children's Privacy</h2>
            <p className="text-glass-secondary leading-relaxed">
              The Service is not intended for children under 13 years of age. We do not knowingly collect 
              information from children under 13. If you believe we have collected such information, please 
              contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Your Rights</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              Depending on your location, you may have rights including:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Access to your personal data</li>
              <li>Correction of inaccurate data</li>
              <li>Deletion of your data</li>
              <li>Data portability</li>
              <li>Objection to processing</li>
              <li>Withdrawal of consent</li>
            </ul>
            <p className="text-glass-secondary leading-relaxed mt-3">
              To exercise these rights, please contact us or use the options in Settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">International Transfers</h2>
            <p className="text-glass-secondary leading-relaxed">
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place to protect your information in compliance 
              with applicable laws.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Changes to This Policy</h2>
            <p className="text-glass-secondary leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes 
              by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Contact Us</h2>
            <p className="text-glass-secondary leading-relaxed">
              If you have questions about this Privacy Policy or our privacy practices, please contact us at{' '}
              <a href="mailto:hello@polishallthethings.com" className="text-glass hover:text-glass-secondary">
                hello@polishallthethings.com
              </a>
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-on-bg font-body"
          >
            ↑ Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
